import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { createAuthSlice } from "./slices/authSlice";
import { createUserSlice, thunkUserIp } from "./slices/userSlice";

export * from "./slices/authSlice";
export * from "./slices/userSlice";

/**
 * Create a configurable Redux store with persistence and optional middlewares
 * @param {Object} options - Configuration options
 * @returns {Object} The configured store and persistor
 */
export const createAppStore = (options = {}) => {
  const {
    reducers = {}, // Additional reducers to include
    middleware = [], // Additional middleware to include
    tokenRole = "member", // Role for token management
    enablePersistence = true, // Whether to enable redux-persist
    storageEngine = storage, // Storage engine for persistence
    devTools = process.env.NODE_ENV !== "production", // Redux devtools settings
    persistWhitelist = ["auth", "user"], // Whitelist for persistence
    afterCreate = null, // Function to dispatch after store creation
  } = options;

  // Create slices with the provided configuration
  const auth = createAuthSlice({
    tokenRole,
  }).reducer;

  const user = createUserSlice({
    tokenRole,
  }).reducer;

  // Combine core reducers with additional reducers
  const rootReducer = combineReducers({
    auth,
    user,
    ...reducers,
  });

  // Setup persistence configuration if enabled
  let finalReducer = rootReducer;
  if (enablePersistence) {
    const persistConfig = {
      key: "root",
      version: 1,
      storage: storageEngine,
      whitelist: persistWhitelist,
    };
    finalReducer = persistReducer(persistConfig, rootReducer);
  }

  // Create the store with all configurations
  const store = configureStore({
    reducer: finalReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: enablePersistence
          ? {
              ignoredActions: [
                FLUSH,
                REHYDRATE,
                PAUSE,
                PERSIST,
                PURGE,
                REGISTER,
              ],
            }
          : true,
      }).concat(middleware),
    devTools,
  });

  // Create persistor if persistence is enabled
  const persistor = enablePersistence ? persistStore(store) : null;

  // Run any post-creation functions
  if (typeof afterCreate === "function") {
    afterCreate(store);
  }

  // Initialize IP address if userSlice exports initializeUserIp
  if (thunkUserIp) {
    store.dispatch(thunkUserIp());
  }

  return { store, persistor };
};

/**
 * Example usage in client app:
 *
 * import { createAppStore, migrateUserdataToUser } from '@smfa/utils/src/store';
 * import { thunk } from 'redux-thunk';
 * import logger from 'redux-logger';
 * import pageContentReducer from './slices/pageContentSlice';
 *
 * const { store, persistor } = createAppStore({
 *   reducers: {
 *     pageContents: pageContentReducer,
 *   },
 *   middleware: [thunk, logger],
 *   afterCreate: (store) => {
 *     // Custom initialization logic
 *   },
 *   // For migration from userdata to user
 *   enablePersistence: true,
 *   persistWhitelist: ['auth', 'user'],
 * });
 *
 * export { store, persistor };
 */
